import { z } from 'zod'

/** The five kinds of file the backend takes, which are also the names of the multipart fields. */
export const UPLOAD_FIELDS = ['requirementDocs', 'referenceImages', 'logos', 'propertyImages', 'existingMenus'] as const
export type UploadField = (typeof UPLOAD_FIELDS)[number]

/** The files to send, by kind. */
export type UploadFiles = Partial<Record<UploadField, readonly File[]>>

export const uploadedFileSchema = z.object({ label: z.string().nullish(), fileName: z.string().nullish(), fileSize: z.number().default(0) })
export type UploadedFileDto = z.infer<typeof uploadedFileSchema>

/** `taskId` is the background task that processes the files; follow it with the sse-tasks API. It is null when nothing needs processing. */
export const uploadResponseSchema = z.object({
  projectId: z.string(),
  totalFiles: z.number().default(0),
  files: z.array(uploadedFileSchema).nullish().transform((files) => files ?? []),
  taskId: z.string().nullish(),
})
export type UploadResponseDto = z.infer<typeof uploadResponseSchema>

const textList = z.array(z.string()).nullish().transform((items) => items ?? [])

/** What the server made of one file. `customDescription` is the owner's own note about it, when the server keeps one. */
export const assetAnalysisSchema = z.object({
  status: z.string().nullish(),
  useFor: textList,
  avoid: textList,
  assetSummary: z.string().nullish(),
  customDescription: z.string().nullish(),
})
export type AssetAnalysisDto = z.infer<typeof assetAnalysisSchema>

/** One file kept for a project. `assetType` is the kind it was uploaded as (requirements, logo, …) and is how the app groups files. */
export const projectAssetSchema = z.object({
  id: z.string(),
  projectId: z.string().nullish(),
  label: z.string().nullish(),
  assetType: z.string().nullish(),
  fileName: z.string().nullish(),
  fileFormat: z.string().nullish(),
  fileSize: z.number().nullish().transform((size) => size ?? 0),
  /** The storage paths of the file (one for each picture it is kept as; a one-page file has one). An older answer sent a single text: it is read as a list of one. */
  fileUrl: z.union([z.array(z.string()), z.string()]).nullish().transform((value) => (Array.isArray(value) ? value : value ? [value] : []).filter((path) => path.trim() !== '')),
  // Sometimes sent as JSON text: read it, and ignore text that is not JSON.
  analysis: z
    .preprocess((value) => {
      if (typeof value !== 'string') return value
      try { return JSON.parse(value) as unknown } catch { return undefined }
    }, assetAnalysisSchema.nullish())
    .nullish(),
  createdBy: z.string().nullish(),
  createdAt: z.string().nullish(),
})
export type ProjectAssetDto = z.infer<typeof projectAssetSchema>

/** The path a file is shown and downloaded from: the first of its paths. */
export const assetPathOf = (asset: { fileUrl?: readonly string[] | string | null }): string => (Array.isArray(asset.fileUrl) ? asset.fileUrl[0] : asset.fileUrl) ?? ''

/** One file's edited analysis, as `PUT /projects/{id}/assets` takes it. */
export interface AssetAnalysisUpdate {
  id: string
  analysis: { useFor: string[]; avoid: string[]; assetSummary: string; customDescription: string }
}

export const projectAssetPageSchema = z.object({
  items: z.array(projectAssetSchema).nullish().transform((items) => items ?? []),
  page: z.number().default(1),
  pageSize: z.number().default(0),
  totalCount: z.number().default(0),
})
